import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import MyModal from "../Common/MyModal";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  AssetKindType,
  useDeleteAssetTypeMutation,
  useGetAssetTypesQuery,
  useLazyGetAssetsQuery,
  useUpsertAssetTypeMutation,
} from "../store/slices/mapsSlice";
import AssetsIcon from "./AssetsIcon";
import {
  formatAssetIconOptionLabel,
  formatAssetKindOptionLabel,
  formatAssetTypeValue,
  getAssetIconValueLabels,
  loadAssetIconOptions,
} from "./assetIconsList";
import { clearAssetTypeOptionCache } from "./assetTypeOptions";

type AssetTypesModalProps = {
  show: boolean;
  onHide: () => void;
};

const emptyEditState = {
  Label: "",
  Value: "",
  Icon: "",
};

const DEFAULT_ASSET_TYPE_ICON = "/default.svg";

type PendingDeleteState = {
  assetType: AssetKindType;
  usageCount: number;
} | null;

export default function AssetTypesModal({ show, onHide }: AssetTypesModalProps) {
  const { data, isFetching } = useGetAssetTypesQuery(
    { limit: 100, offset: 0 },
    { skip: !show }
  );
  const [saveAssetType, saveResult] = useUpsertAssetTypeMutation();
  const [deleteAssetType, deleteResult] = useDeleteAssetTypeMutation();
  const [getAssetsUsage, assetsUsageResult] = useLazyGetAssetsQuery();
  const [editing, setEditing] = useState<Partial<AssetKindType>>(emptyEditState);
  const [isCreating, setIsCreating] = useState(false);
  const [replacementType, setReplacementType] = useState("");
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState>(null);

  useEffect(() => {
    if (!show) {
      setEditing(emptyEditState);
      setIsCreating(false);
      setReplacementType("");
      setPendingDelete(null);
    }
  }, [show]);

  const assetTypes = data?.types || [];
  const isEditing = !!editing.Value && !isCreating;
  const isEditingOrCreating = isEditing || isCreating;

  const getReplacementOptions = (assetType: AssetKindType) =>
    assetTypes
      .filter((type) => type.Value && type.Value !== assetType.Value)
      .map((type) => ({
        label: type.Label || type.Value,
        value: type.Value,
        icon: type.Icon || type.Value,
        isDefault: !!type.IsDefault,
      }));

  const handleEdit = (assetType: AssetKindType) => {
    setPendingDelete(null);
    setReplacementType("");
    setIsCreating(false);
    setEditing({
      Label: assetType.Label,
      Value: assetType.Value,
      Icon: assetType.Icon,
    });
  };

  const handleCreate = () => {
    setPendingDelete(null);
    setReplacementType("");
    setIsCreating(true);
    setEditing({
      Label: "",
      Value: "",
      Icon: DEFAULT_ASSET_TYPE_ICON,
    });
  };

  const resetEditor = () => {
    setEditing(emptyEditState);
    setIsCreating(false);
  };

  const handleSave = async () => {
    const label = editing.Label?.trim() || "";
    const value = isCreating ? formatAssetTypeValue(label) : editing.Value?.trim() || "";

    if (!label || !value) {
      toast.error("Asset type label is required");
      return;
    }
    if (!editing.Icon) {
      toast.error("Asset type icon is required");
      return;
    }
    if (
      isCreating &&
      assetTypes.some((assetType) => assetType.Value === value)
    ) {
      toast.error("Asset type already exists");
      return;
    }

    const response: any = await saveAssetType({
      Label: label,
      Value: value,
      Icon: editing.Icon,
    });
    if (response.error) {
      toast.error("Asset type could not be saved");
      return;
    }

    toast.success("Asset type saved successfully");
    clearAssetTypeOptionCache([value]);
    resetEditor();
  };

  const handleDelete = async (assetType: AssetKindType) => {
    const usageResponse: any = await getAssetsUsage({
      page: 1,
      perPage: 1,
      kind: assetType.Value,
    });
    if (usageResponse.error) {
      toast.error("Asset type usage could not be checked");
      return;
    }

    const usageCount = usageResponse.data?.total || 0;

    setEditing(emptyEditState);
    setReplacementType("");
    setPendingDelete({ assetType, usageCount });
  };

  const deleteType = async (assetType: AssetKindType, replacementValue = "") => {
    const response: any = await deleteAssetType({
      value: assetType.Value,
      replacementValue,
    });
    if (response.error) {
      toast.error("Asset type could not be deleted");
      return;
    }

    if (editing.Value === assetType.Value) {
      resetEditor();
    }
    clearAssetTypeOptionCache(
      replacementValue ? [assetType.Value, replacementValue] : [assetType.Value]
    );
    setReplacementType("");
    setPendingDelete(null);
    toast.success(
      assetType.IsDefault
        ? "Asset type hidden successfully"
        : "Asset type deleted successfully"
    );
  };

  const handleCancelDelete = () => {
    setPendingDelete(null);
    setReplacementType("");
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    if (pendingDelete.usageCount > 0 && !replacementType) {
      toast.error("Select a replacement asset type");
      return;
    }

    await deleteType(pendingDelete.assetType, replacementType);
  };

  return (
    <>
    <MyModal
      label={null}
      heading="Manage asset types"
      show={show}
      onHide={onHide}
      size="lg"
      centered
    >
        <div className="row g-4">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="fw-bold mb-0">Asset types</h6>
              <div className="d-flex align-items-center gap-2">
                {isFetching && <Spinner animation="border" size="sm" />}
                <Button variant="outline-secondary" size="sm" onClick={handleCreate}>
                  Add asset type
                </Button>
              </div>
            </div>
            <div className="list-group" style={{ maxHeight: 460, overflowY: "auto" }}>
              {assetTypes.map((assetType) => {
                const canDelete = !assetType.IsDefault && assetType.ClientId !== 0;
                const canHide = !!assetType.IsDefault;
                const canRemove = canDelete || canHide;
                return (
                  <div
                    key={`${assetType.ClientId}-${assetType.Value}`}
                    className="list-group-item d-flex align-items-center gap-3"
                  >
                    <AssetsIcon assetKind={assetType.Icon || assetType.Value} width={24} />
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-bold text-truncate">
                        {assetType.Label || assetType.Value}
                      </div>
                    </div>
                    {assetType.IsDefault && (
                      <span className="badge text-bg-light">System-type</span>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      title="Edit asset type"
                      onClick={() => handleEdit(assetType)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title={
                        canHide
                          ? "Hide system-type asset type"
                          : canDelete
                          ? "Delete asset type"
                          : "Asset type cannot be deleted"
                      }
                      disabled={
                        !canRemove || deleteResult.isLoading || assetsUsageResult.isFetching
                      }
                      onClick={() => handleDelete(assetType)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                );
              })}
              {!isFetching && assetTypes.length === 0 && (
                <div className="list-group-item text-secondary">
                  No asset types found.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
        </div>
    </MyModal>
    <MyModal
      label={null}
      heading={isCreating ? "Add asset type" : "Edit asset type"}
      show={isEditingOrCreating}
      onHide={resetEditor}
      centered
    >
        <div className="d-flex flex-column gap-3">
          <Input
            label="Label"
            value={[editing.Label || ""]}
            onChange={([label]: any) =>
              setEditing((current) => ({ ...current, Label: label }))
            }
          />
          <Input
            label="Icon"
            value={editing.Icon ? [editing.Icon] : []}
            onChange={([icon]: any) =>
              setEditing((current) => ({ ...current, Icon: icon }))
            }
            inputType="select"
            options={undefined}
            loadOptions={loadAssetIconOptions as any}
            formatOptionLabel={formatAssetIconOptionLabel}
            getValueLabels={getAssetIconValueLabels as any}
            isClearable
            placeholder="Select icon"
          />
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              onClick={resetEditor}
            >
              Cancel
            </Button>
            <Button
              variant="colored"
              onClick={handleSave}
              disabled={saveResult.isLoading}
            >
              Save
            </Button>
          </div>
        </div>
    </MyModal>
    <MyModal
      label={null}
      heading="Delete asset type"
      show={!!pendingDelete}
      onHide={handleCancelDelete}
      centered
    >
        {pendingDelete && (
          <div className="d-flex flex-column gap-3">
            <div className="small text-secondary">
              {pendingDelete.usageCount > 0 ? (
                <>
                  Asset type{" "}
                  <strong>{pendingDelete.assetType.Label || pendingDelete.assetType.Value}</strong>{" "}
                  is used in <strong>{pendingDelete.usageCount}</strong>{" "}
                  {pendingDelete.usageCount === 1 ? "asset" : "assets"}.
                </>
              ) : (
                <>
                  Asset type{" "}
                  <strong>{pendingDelete.assetType.Label || pendingDelete.assetType.Value}</strong>{" "}
                  is not used.
                </>
              )}
            </div>
            {pendingDelete.usageCount > 0 &&
            getReplacementOptions(pendingDelete.assetType).length > 0 ? (
              <Input
                label="Change assets to"
                value={replacementType ? [replacementType] : []}
                onChange={([value]: any) => setReplacementType(value || "")}
                inputType="select"
                options={getReplacementOptions(pendingDelete.assetType)}
                formatOptionLabel={formatAssetKindOptionLabel}
                isClearable
                placeholder="Select replacement type"
              />
            ) : pendingDelete.usageCount > 0 ? (
              <div className="small text-secondary">
                No other asset types are available for replacement.
              </div>
            ) : null}
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button variant="outline-secondary" onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button
                variant="colored"
                onClick={handleConfirmDelete}
                disabled={
                  deleteResult.isLoading ||
                  !!(pendingDelete && pendingDelete.usageCount > 0 && !replacementType)
                }
              >
                {pendingDelete?.assetType.IsDefault
                  ? pendingDelete.usageCount > 0
                    ? "Change and delete"
                    : "Delete"
                  : pendingDelete && pendingDelete.usageCount > 0
                  ? "Change and delete"
                  : "Delete"}
              </Button>
            </div>
          </div>
        )}
    </MyModal>
    </>
  );
}
